import React from 'react';

import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { SwiperSlide } from 'swiper/react';

import {
	CalendarIcon,
	ClockFilledIcon,
	CupFilledIcon,
	DiamondIcon,
	GiftIcon,
	LogoutIcon,
	RefreshIcon,
	UsersIcon
} from '@icons';
import gtmTracker from '@utils/gtmTracker';

import { useModals } from '@shared/contexts/modal-context';
import useGetPointsIcon from '@shared/hooks/use-get-points-icon';
import { useMediaQuery } from '@shared/hooks/use-media-query';
import { BREAKPOINTS } from '@shared/styled-system';
import { ROUTES } from '@shared/utils/routes';

import { useMutationJoinCoinRace, useMutationLeaveCoinRace, useQueryCoinRaceDetails } from '@hooks/api/coin-races';
import { useQueryGetMe } from '@hooks/api/user/use-query-me';

import { COIN_RACE_STATUS, COIN_RACE_TYPE } from '@types';

import { Button } from '@components/button';
import { ModalName } from '@components/modals/types';
import { Slider } from '@components/slider';

import * as Styled from './coin-race-details.styled';
import { TimerBlock, TopWinnerCard, TopWinnersContainer } from './components';
import { JackpotCounter } from './components/jackpot-counter';
import { slideMediaConfig } from './config';
import { formatDateRange, formatDuration, getBannerImage, getClockColorByDuration } from './helper';

type OwnProps = {
	id: string;
};

const CoinRaceDetails: React.FC<OwnProps> = ({ id }) => {
	const { t } = useTranslation('coin_race_details');

	const { data, isLoading, isError, refetch, isFetching } = useQueryCoinRaceDetails({ id });
	const joinMutation = useMutationJoinCoinRace();
	const leaveMutation = useMutationLeaveCoinRace();
	const { openModal } = useModals();
	const { data: user } = useQueryGetMe();
	const pointsIcon = useGetPointsIcon();
	const myPosition = data?.current_user?.position;

	const isAuthenticated = !!user?.pm_id;
	const isMobile = useMediaQuery(BREAKPOINTS.NOT_DESKTOP);

	if (isLoading) {
		return <div>Loading...</div>;
	}

	if (isError || !data) {
		return <Navigate to={ROUTES.COIN_RACE} />;
	}

	const { coin_race, prizes, leaderboard } = data;
	const endDate = coin_race?.end_date || new Date().toISOString();
	const clockColor = coin_race ? getClockColorByDuration(coin_race.duration_seconds) : '#297AFF';

	const isPendingRace = coin_race?.status === COIN_RACE_STATUS.PENDING;
	const isFinishedRace = coin_race?.status === COIN_RACE_STATUS.COMPLETED;
	const isTypeJackpot = coin_race.reward_type === COIN_RACE_TYPE.JACKPOT_PRIZE;

	const isJoinButtonVisible = coin_race.can_subscribe && !coin_race.subscribed;
	const isLeaveButtonVisible = coin_race.subscribed && !isFinishedRace;

	const topWinnersCount = prizes.length > 0 ? Math.min(leaderboard.length, 3) : 0;
	const topWinners = leaderboard.slice(0, topWinnersCount);
	const remainingLeaderboard = leaderboard.slice(topWinnersCount);
	const hasPrizes = prizes.length > 0;

	const handleTrackEvent = (label: string) => {
		gtmTracker.trackEvent('click', {
			event_category: 'Coin_race_details',
			event_action: 'Click',
			event_label: label
		});
	};

	const handleJoin = () => {
		handleTrackEvent('Join');
		if (!isAuthenticated) {
			openModal(ModalName.LOGIN);
			return;
		}
		joinMutation.mutate(
			{ id },
			{
				onSuccess: () => openModal(ModalName.JOIN_RACE_CONFIRM, { raceName: coin_race.name }),
				onError: error => console.error('Failed to join race:', error)
			}
		);
	};

	const handleLeave = () => {
		handleTrackEvent('Leave');
		openModal(ModalName.CONFIRM, {
			title: t('leave_action'),
			message: t('modals.leave_confirmation'),
			confirmButtonText: t('leave_action'),
			cancelButtonText: t('cancel', { ns: 'main' }),
			onConfirm: async () => {
				await leaveMutation.mutateAsync({ id });
			}
		});
	};

	const handleRulesClick = () => {
		if (!coin_race.rules_and_conditions) return;
		handleTrackEvent('Rules');
		openModal(ModalName.COIN_RACE_RULES, {
			title: coin_race.name,
			content: coin_race.rules_and_conditions
		});
	};

	const handlePrizeDetailsClick = (prize: (typeof prizes)[number]) => {
		if (!prize.description) return;

		handleTrackEvent('Prize Details');

		const prizePlace =
			prize.winner_range_from === prize.winner_range_to
				? `${prize.winner_range_from} ${t('place')}`
				: `${prize.winner_range_from}-${prize.winner_range_to} ${t('place')}`;

		openModal(ModalName.COIN_RACE_RULES, {
			title: `${prizePlace} ${t('prize').toLowerCase()}:  ${prize.reward_value}`,
			content: prize.description
		});
	};

	const handleRefreshLeaderboard = () => {
		handleTrackEvent('Refresh Leaderboard');
		refetch();
	};

	const calculateJackpotPrize = (percent: number, winnersCount: number) => {
		const jackpotTotal = Number(coin_race?.jackpot_points || 0);
		const totalPrizeForTier = (jackpotTotal * percent) / 100;
		return Math.floor(totalPrizeForTier / winnersCount);
	};

	return (
		<Styled.Container>
			<Styled.TopSection $isFinished={isFinishedRace}>
				<Styled.TopSectionContent>
					<Styled.LeftBlock>
						<Styled.MetaInfo>
							{isFinishedRace && <Styled.StatusBadge>{t('finished_f')}</Styled.StatusBadge>}
							<Styled.MetaItem>
								<CalendarIcon />
								<span>{formatDateRange(coin_race.start_date, coin_race.end_date)}</span>
							</Styled.MetaItem>
							{!isFinishedRace && (
								<Styled.MetaItem $iconColor={clockColor}>
									<Styled.Duration>
										{t('duration')}: <span>{formatDuration(coin_race.duration_seconds, t)}</span>
									</Styled.Duration>
									<ClockFilledIcon />
								</Styled.MetaItem>
							)}
							{isTypeJackpot && (
								<Styled.MetaItem $iconColor={clockColor}>
									<DiamondIcon />
									<Styled.Jackpot>Jackpot</Styled.Jackpot>
								</Styled.MetaItem>
							)}
						</Styled.MetaInfo>

						<Styled.RaceName>{coin_race.name}</Styled.RaceName>

						{coin_race.description && <Styled.Description>{coin_race.description}</Styled.Description>}

						{isTypeJackpot ? (
							<Styled.MobileJackpotWrapper>
								<JackpotCounter amount={Number(coin_race.jackpot_points || 0)} isCompleted={isFinishedRace} />
							</Styled.MobileJackpotWrapper>
						) : (
							<Styled.MobileCupImage src={getBannerImage(coin_race.reward_type, isFinishedRace)} alt='Cup' />
						)}

						<TimerBlock endDate={endDate} isPendingRace={isPendingRace} />

						<Styled.Actions>
							{isJoinButtonVisible && (
								<Button color='primary' onClick={handleJoin} disabled={joinMutation.isPending}>
									{joinMutation.isPending ? t('loading_common') : t('join')}
								</Button>
							)}
							{isLeaveButtonVisible && (
								<Button color='dark' onClick={handleLeave} disabled={leaveMutation.isPending}>
									<Styled.LeaveButtonContent>
										<span>{leaveMutation.isPending ? t('loading_common') : t('leave_action')}</span>
										<LogoutIcon />
									</Styled.LeaveButtonContent>
								</Button>
							)}
							<Button color='dark' onClick={handleRulesClick}>
								{t('rules_title')}
							</Button>
						</Styled.Actions>
					</Styled.LeftBlock>

					<Styled.RightBlock>
						{isTypeJackpot ? (
							<JackpotCounter amount={Number(coin_race.jackpot_points || 0)} isCompleted={isFinishedRace} />
						) : (
							<Styled.CupImage src={getBannerImage(coin_race.reward_type, isFinishedRace)} alt='Cup' />
						)}
					</Styled.RightBlock>
				</Styled.TopSectionContent>
			</Styled.TopSection>

			<Styled.ContentWrapper>
				<Styled.Section>
					<Styled.SectionTitle>{t('prizes')}</Styled.SectionTitle>
					{hasPrizes && (
						<Slider spaceBetween={12} pagination={false} navigation={!isMobile} breakpoints={slideMediaConfig}>
							{prizes.map(prize => {
								const isPointIcon = Number(prize.reward_value) >= 0 && pointsIcon;
								let displayValue = prize.reward_value;

								if (isTypeJackpot) {
									const winnersCount = prize.winner_range_to - prize.winner_range_from + 1;
									displayValue = calculateJackpotPrize(Number(prize.reward_value || 0), winnersCount);
								}

								return (
									<SwiperSlide
										key={`${prize.position}-${prize.winner_range_from}`}
										onClick={() => handlePrizeDetailsClick(prize)}
									>
										<Styled.PrizeCard $isClickable={Boolean(prize.description)}>
											<Styled.PrizeInfo>
												<Styled.PrizePosition>
													{prize.winner_range_from === prize.winner_range_to
														? `${prize.winner_range_from} ${t('place')}`
														: `${prize.winner_range_from}-${prize.winner_range_to} ${t('place')}`}
												</Styled.PrizePosition>
												<Styled.PrizeValue>
													{displayValue} {isPointIcon && pointsIcon}
												</Styled.PrizeValue>
											</Styled.PrizeInfo>
											<Styled.PrizeImageBlock>
												{prize.image && <img src={prize.image} alt={`${t('prize')} ${prize.position}`} />}
											</Styled.PrizeImageBlock>
											{prize.description && <Styled.PrizeInfoIcon />}
										</Styled.PrizeCard>
									</SwiperSlide>
								);
							})}
						</Slider>
					)}
				</Styled.Section>

				{!isPendingRace && (
					<Styled.Section>
						<Styled.SectionTitle>{t(isFinishedRace ? 'winners' : 'leaders')}</Styled.SectionTitle>
						{leaderboard.length === 0 ? (
							<Styled.EmptyMessage>{t('no_participants')}</Styled.EmptyMessage>
						) : (
							<>
								{topWinners.length > 0 && (
									<TopWinnersContainer>
										{topWinners.map((winner, idx) => {
											const isCurrentUser = myPosition !== undefined && winner.position === myPosition;

											return (
												<TopWinnerCard
													key={`${winner.user_pm_id}-${idx}`}
													winner={winner}
													place={idx + 1}
													isMe={isCurrentUser}
												/>
											);
										})}
									</TopWinnersContainer>
								)}

								<Styled.RefreshLeaderboard>
									<Button color='dark' onClick={handleRefreshLeaderboard} disabled={isFetching}>
										{isFetching ? t('loading_common') : t('refresh')} <RefreshIcon />
									</Button>
								</Styled.RefreshLeaderboard>

								{remainingLeaderboard.length > 0 && (
									<>
										<Styled.SectionTitle>{t('participants')}</Styled.SectionTitle>
										<Styled.LeaderboardTable>
											<Styled.TableHeader>
												<Styled.TableCell>
													<CupFilledIcon /> <Styled.HeaderText>{t('place')}</Styled.HeaderText>
												</Styled.TableCell>
												<Styled.TableCell>
													<UsersIcon /> <Styled.HeaderText>{t('participants')}</Styled.HeaderText>
												</Styled.TableCell>
												<Styled.TableCell>
													{pointsIcon} <Styled.HeaderText>{t('points')}</Styled.HeaderText>
												</Styled.TableCell>
												<Styled.TableCell>
													<GiftIcon /> <Styled.HeaderText>{t('prize')}</Styled.HeaderText>
												</Styled.TableCell>
											</Styled.TableHeader>
											{remainingLeaderboard.map(entry => {
												const matchingPrize = prizes.find(
													p => entry.position >= p.winner_range_from && entry.position <= p.winner_range_to
												);
												let displayReward = entry.reward_value;
												const isCurrentUser = myPosition !== undefined && entry.position === myPosition;

												if (isTypeJackpot && matchingPrize) {
													const winnersCount = matchingPrize.winner_range_to - matchingPrize.winner_range_from + 1;
													displayReward = calculateJackpotPrize(Number(matchingPrize.reward_value || 0), winnersCount);
												}

												return (
													<Styled.TableRow key={`${entry.user_pm_id}-${entry.position}`} $isCurrentUser={isCurrentUser}>
														<Styled.TableCell>{entry.position}</Styled.TableCell>
														<Styled.TableCell>
															<Styled.TextEllipsis $isMe={isCurrentUser}>{entry.user_pm_id}</Styled.TextEllipsis>
														</Styled.TableCell>
														<Styled.TableCell>{entry.score}</Styled.TableCell>
														<Styled.TableCell>
															<Styled.TextEllipsis>{displayReward}</Styled.TextEllipsis>
														</Styled.TableCell>
													</Styled.TableRow>
												);
											})}
										</Styled.LeaderboardTable>
									</>
								)}
							</>
						)}
					</Styled.Section>
				)}
			</Styled.ContentWrapper>
		</Styled.Container>
	);
};

export default CoinRaceDetails;
